import {
  AttachmentOutlined,
  CloseOutlined,
  InsertEmoticonOutlined,
  InsertLinkOutlined,
  PermIdentityOutlined,
  SendOutlined,
} from '@mui/icons-material';
import { Fragment, useEffect, useState } from 'react';
import TextEditor from './TextEditor';
import { Dialog, Transition } from '@headlessui/react';
import { sendEmailToTenants } from './DataServices/ProspectiveTenantDataService';
import Notification from './Notification';
import Loading from './Loading';
import { IEmailAttachFile } from './Models';

interface ComposeEmailProps {
  emails: Set<{ email: string, dept: string }>;
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedEmails: Set<{ email: string, dept: string }>;
}

const ComposeEmail = ({ emails, open, setOpen, selectedEmails }: ComposeEmailProps) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState<string>('');
  const [allTo, setAllTo] = useState<Set<{ email: string, dept: string }>>(new Set());
  const [filteredTo, setFilteredTo] = useState<Set<{ email: string, dept: string }>>(new Set());
  const [ccEmails, setCcEmails] = useState<Set<string>>(new Set());
  const [isToFocused, setIsToFocused] = useState<boolean>(true);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [joinEmails, setJoinEmails] = useState('');
  const [notificationMessage, setNotificationMessage] = useState("");
  const [docs, setDocs] = useState<IEmailAttachFile[]>([]);
  const [notificationMessageType, setNotificationMessageType] = useState("success");
  const [openNotification, setOpenNotification] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAllTo(selectedEmails);
    var newAppartmentAsSubject = "";
    selectedEmails.forEach(item => {
      newAppartmentAsSubject += item.dept + ", ";
    })
    setSubject(newAppartmentAsSubject);
  }, [selectedEmails, open]);

  useEffect(() => {
    const emailExistsInSet = (set: Set<{ email: string, dept: string }>, email: string) => {
      var isEmailExist = false;
      set.forEach(item => {
        if (item.email === email) {
          isEmailExist = true;
        }
      })
      return isEmailExist;
    }
    const newFilteredTo = new Set<{ email: string, dept: string }>();
    emails.forEach((emailObj) => {
      if (!emailExistsInSet(allTo, emailObj.email)) {
        newFilteredTo.add(emailObj);
      }
    });
    setFilteredTo(newFilteredTo);
  }, [allTo, open]);

  const handleSend = async () => {
    var notificationMessageValue = "";
    try {
      // Prepare cc emails for sending
      setLoading(true);
      const ccEmailsArr = [...Array.from(ccEmails)];

      // Remove trailing commas from subject and split
      const newSubject = subject.split(', ');
      var isSubjectChanged = false;
      let i = 0;

      allTo.forEach(item => {
        if (item.dept !== newSubject[i++]) {
          isSubjectChanged = true;
          return;
        }
      })
      // Check if the number of subjects matches the number of recipients
      if (isSubjectChanged) {
        allTo.forEach(item => {
          item.dept = subject; // Assign the entire subject if not matched
        });
      }
      // Send email logic here
      const emailData = {
        keypairOfToAndSuject: Array.from(allTo).map(item => ({ email: item.email, subject: item.dept })),
        cc: ccEmailsArr,
        body: body
      };

      if ((emailData.keypairOfToAndSuject.length === 0 || cc.length === 0) && body.length === 0) {
        notificationMessageValue = "Email or Body is missing!";
        throw Error("Something Missing");
      }
      await sendEmailToTenants(emailData);
      if (notificationMessageValue.length === 0) notificationMessageValue = "Emails send successfully!";
      setCcEmails(new Set());
      setAllTo(new Set());
      setFilteredTo(new Set());
      setTo('');
      setCc('');
      setBody('');
      setSubject('');
      setJoinEmails('');
      setLoading(false);
      setOpen(false);
      setOpenNotification(true);
      setNotificationMessage(notificationMessageValue);
      setNotificationMessageType("success");
      setOpen(false);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    } catch (error) {
      if (notificationMessageValue.length === 0) notificationMessageValue = "Failed to send Email!";
      console.log("Error: ", error);
      setCcEmails(new Set());
      setAllTo(new Set());
      setFilteredTo(new Set());
      setTo('');
      setCc('');
      setBody('');
      setSubject('');
      setJoinEmails('');
      setOpenNotification(true);
      setLoading(false);
      setNotificationMessage(notificationMessageValue);
      setNotificationMessageType("error");
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    }
  }

  const handleRemoveEmailFromTo = (email: { email: string, dept: string }) => {
    const newTo = new Set(allTo);
    newTo.delete(email);
    setAllTo(newTo);
    var newAppartmentAsSubject = "";
    var isDeptEmpty = false;
    newTo.forEach(item => {
      newAppartmentAsSubject += item.dept + ", ";
      if (item.dept.length === 0) isDeptEmpty = true;
    })
    if (!isDeptEmpty) setSubject(newAppartmentAsSubject);
  };

  const handleAddEmailInAllTo = (email: { email: string, dept: string }) => {
    setAllTo((prev) => new Set(prev).add(email));
    if (email.dept.length > 0) {
      var newAppartmentAsSubject = subject;
      newAppartmentAsSubject += (email.dept + ", ");
      setSubject(newAppartmentAsSubject);
    } else {
      setSubject("");
    }
    setTo('');
  };

  const handleCcEmails = () => {
    setCcEmails((prev) => new Set(prev).add(cc)); // Assuming dept is not required for cc
    setCc('');
  };

  const handleRemoveEmailFromCc = (email: string) => {
    const newCc = new Set(ccEmails);
    newCc.delete(email);
    setCcEmails(newCc);
  };

  const handleIsToFocused = (isFocused: boolean) => {
    const combinedEmails = [
      ...Array.from(allTo).map(item => item.email),
      ...Array.from(ccEmails)
    ].slice(0, 2);
    setJoinEmails(combinedEmails.join(', '));
    setIsToFocused(isFocused);
  }

  const handleCloseComposeEmail = () => {
    setOpen(false);
    setCcEmails(new Set());
    setAllTo(new Set());
    setFilteredTo(new Set());
    setTo('');
    setCc('');
    setBody('');
    setSubject('');
    setJoinEmails('');
    setIsToFocused(true);
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-15 w-screen overflow-y-auto">
            <div className="flex min-h-full min-w-full justify-center text-center items-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[550px]">
                  <div className="flex flex-col w-full rounded-lg">
                    <div className="flex items-center justify-between p-2 bg-blue-100 rounded-t-lg">
                      <h1 className="font-medium">New Message</h1>
                      <button onClick={() => handleCloseComposeEmail()}>
                        <CloseOutlined className="cursor-pointer active:translate-y-[1px]" fontSize="small" />
                      </button>
                    </div>
                    <div className="flex flex-col px-4 py-2">
                      {
                        isToFocused ?
                          <div className="flex flex-col border-b border-gray-300">
                            <div className="relative w-full px-2 flex flex-wrap items-center gap-2 text-sm text-gray-700 focus:outline-none">
                              <div className="pr-2">To: </div>
                              {Array.from(allTo).map((email, index) => (
                                <div key={index} className="flex gap-1 shadow p-1 border rounded-full items-center justify-center">
                                  <div className="flex items-center justify-center border rounded-full overflow-hidden">
                                    <PermIdentityOutlined fontSize="small" />
                                  </div>
                                  <span className="font-medium">{email.email}</span>
                                  <div
                                    className="flex items-center justify-center rounded-full overflow-hidden cursor-pointer"
                                    onClick={() => handleRemoveEmailFromTo(email)}
                                  >
                                    <CloseOutlined fontSize="small" />
                                  </div>
                                </div>
                              ))}
                              <input
                                type="text"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleAddEmailInAllTo({ email: to, dept: '' });
                                }}
                                placeholder=""
                                className={
                                  'p-2 text-sm text-gray-700 border-none focus:outline-none focus:ring-0 ' +
                                  (to.length > 10 ? 'w-max' : 'w-24')
                                }
                                autoFocus
                              />
                              <div className="absolute border top-20 z-10 right-0 bg-white max-h-64 overflow-auto shadow-md rounded-md">
                                {to.length > 0 &&
                                  Array.from(filteredTo).map((item, index) => (
                                    <div
                                      className="flex gap-2 items-center border-b hover:bg-gray-200 cursor-pointer p-2"
                                      key={index}
                                      onClick={() => handleAddEmailInAllTo(item)}
                                    >
                                      <div className="flex items-center justify-center border rounded-full overflow-hidden">
                                        <PermIdentityOutlined fontSize="small" />
                                      </div>
                                      <span className="font-medium">{item.email}</span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                            <div className="relative w-full px-2 mb-2 flex flex-wrap items-center gap-2 text-sm text-gray-700 focus:outline-none">
                              <div className="pr-2">Cc: </div>
                              {Array.from(ccEmails).map((email, index) => (
                                <div key={index} className="flex gap-1 shadow p-1 border rounded-full items-center justify-center">
                                  <div className="flex items-center justify-center border rounded-full overflow-hidden">
                                    <PermIdentityOutlined fontSize="small" />
                                  </div>
                                  <span className="font-medium">{email}</span>
                                  <div
                                    className="flex items-center justify-center rounded-full overflow-hidden cursor-pointer"
                                    onClick={() => handleRemoveEmailFromCc(email)}
                                  >
                                    <CloseOutlined fontSize="small" />
                                  </div>
                                </div>
                              ))}
                              <input
                                type="text"
                                value={cc}
                                onChange={(e) => setCc(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleCcEmails();
                                }}
                                className="p-2 text-sm text-gray-700 border-none focus:outline-none focus:ring-0"
                              />
                            </div>
                          </div> :
                          <div className='flex items-center gap-1 p-2 text-gray-800 border-b border-gray-300' onClick={() => handleIsToFocused(true)}>
                            {joinEmails}
                            <span className='border rounded px-0.5 bg-gray-100 text-gray-500'>{allTo.size + ccEmails.size > 2 && (allTo.size + ccEmails.size - 2) + " more"}</span>
                          </div>
                      }
                      <div className="border-b border-gray-300">
                        <input
                          type="text"
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          onFocus={() => handleIsToFocused(false)}
                          placeholder="Subject:"
                          className="w-full p-2 font-medium border-none text-sm text-gray-700  focus:outline-none focus:ring-0"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col px-4 w-full" onClick={() => handleIsToFocused(false)}>
                      <TextEditor
                        content={body}
                        setContent={setBody}
                        docs={docs}
                        setDocs={setDocs}
                      />
                      <div className="flex items-center justify-end py-4">
                        <div className="flex items-center">
                          <button
                            className="flex gap-2 items-center justify-center bg-[#0b57d0] hover:shadow-md active:translate-y-[1px] hover:shadow-blue-400 hover:transition-shadow text-white font-medium py-1.5 px-4 rounded-full"
                            onClick={handleSend}
                          >
                            Send
                            <SendOutlined fontSize="inherit" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {loading && <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
                    <Loading />
                  </div>}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <Notification
        isOpen={openNotification}
        setIsOpen={setOpenNotification}
        message={notificationMessage}
        messageType={notificationMessageType}
      />
    </>
  );
};

export default ComposeEmail;
